/**
 * useCreatorStatus — single source of truth for "where is this user in the
 * creator program?". Every creator CTA in the app reads from this so no two
 * surfaces can contradict each other.
 */
import { useMyCreatorProfile } from "@/hooks/useCreators";
import { useAuth } from "@/hooks/useAuth";

export type CreatorStatus = "none" | "pending" | "verified" | "rejected";

export interface CreatorCtaCopy {
  status: CreatorStatus;
  /** Short label for menus / buttons. */
  label: string;
  /** Stable label for navigation entries, so the hub is always findable. */
  menuLabel: string;
  /** One-line plain status, shown under the menu label. */
  statusLine: string;
  /** Longer supporting line for cards. */
  description: string;
  /** Where the CTA should go. */
  to: string;
  isLoading: boolean;
  /** True only for a verified creator (can submit posts). */
  isCreator: boolean;
  /** True when signed out — callers decide whether to show a sign-in gate. */
  isGuest: boolean;
}

const COPY: Record<
  CreatorStatus,
  { label: string; menuLabel: string; statusLine: string; description: string; to: string }
> = {
  none: {
    label: "Become a Creator",
    menuLabel: "Creator hub",
    statusLine: "Not joined yet — see what creators get",
    description: "Get credited on the places you post about across the UAE.",
    to: "/creators/join",
  },
  pending: {
    label: "Creator application in review",
    menuLabel: "Creator hub",
    statusLine: "Application in review",
    description: "Our team is checking your profile. We'll notify you when it's approved.",
    to: "/me/contributor",
  },
  verified: {
    label: "My Creator Hub",
    menuLabel: "Creator hub",
    statusLine: "Verified creator",
    description: "Add your posts to trails and track your live credits.",
    to: "/me/contributor",
  },
  rejected: {
    label: "Update your creator profile",
    menuLabel: "Creator hub",
    statusLine: "Profile needs an update",
    description: "Your profile needs a few changes before we can verify it.",
    to: "/creators/join",
  },
};

export function useCreatorStatus(): CreatorCtaCopy {
  const { user } = useAuth();
  const { data: profile, isLoading } = useMyCreatorProfile();

  const status: CreatorStatus = !user
    ? "none"
    : profile
      ? ((profile.status === "verified"
          ? "verified"
          : profile.status === "rejected"
            ? "rejected"
            : "pending") as CreatorStatus)
      : "none";

  return {
    status,
    ...COPY[status],
    isLoading: !!user && isLoading,
    isCreator: status === "verified",
    isGuest: !user,
  };
}
